import { useEffect, useMemo, useState } from "react";
import defaultAvatar from "@/assets/default_avatar.svg";
import { PHOTOS_BASE_URL } from "@/util/api";
import { getRefListNameByID } from "@/util/common_function";
import { useVocab } from "@/hooks/useVocab";
import {
  fetchJacketBookingSections,
  fetchJacketWarrants,
} from "./reportSectionsApi";
import {
  ArrestBlock,
  ReportSectionBlock,
  ReportSectionHeaderBar,
  ReportEmptyMessage,
  ReportSectionTable,
} from "./styles";

// Shared building blocks for every "full jacket report" entry point in
// Super Search — the Jackets module's own result rows carry a nested bio
// straight from the search response, while Bookings/Warrants module rows
// only carry a bookingNumber/warrantNumber + jacketId and have to fetch the
// bio and jacket-scoped sections themselves. Both paths render the exact
// same markup here so the ASP report (reports/JacketSummaryNew.asp) looks
// identical no matter which module you searched from — which matches ASP
// itself: InvestigatorsViewInterface.asp's ListButtonOverride_Details always
// sets fieldSourceToGet = "Jackets" and opens the one Jacket Summary Report,
// regardless of whether you got to it via the Jackets, Bookings, or
// Warrants tab.

export const capitalize = (str) =>
  str ? str.charAt(0).toUpperCase() + str.slice(1) : "";

export const getPhotoPath = (path) => {
  if (!path) return "";
  if (path.includes("\\")) {
    const splitPath = path.split("/");
    if (splitPath.length === 4) {
      splitPath.shift();
      splitPath.shift();
    }
    return splitPath.join("\\");
  }
  return path;
};

// Section list, in the exact same order as reports/JacketSummaryNew.asp
// (Distinguishing Characteristics through Visitor Log (My Visits) — the
// Bookings-gated section that includes both Visitor Log headers). Each
// entry's "columns" describe the table it renders *when it has rows* —
// per the client's own screenshots, ASP shows a real table with headers
// only when a section has data; an empty section shows no table at all,
// just "There is no data to display." directly under the header bar. This
// mirrors that exactly.
//
// "arrests" (+ its nested Charges/Detentions), "warrants", "holds",
// "victimNotifications", "inmateAssociates", and "distinguishingCharacter-
// istics" are wired to real data (see reportSectionsApi.js and
// mapDistinguishingCharacteristics below) — verified against an actual GET
// .../bookingNumber/id/{id} payload, not just guessed from ASP's columns.
// Everything else still has no jacket-scoped list endpoint in the current
// backend (most of ASP's other sections come from bespoke SQL joins or the
// legacy TableView engine with no React-app equivalent yet — "associated
// WithInmates" specifically is the *reverse* direction of inmateAssociates,
// which has no reverse lookup available), so those still always render the
// same empty-state row ASP itself would show for an empty/disabled section.
// That's a real gap, not a bug — flagged so it's not mistaken for "this
// person has no records."
export const SECTION_DEFS = [
  {
    key: "distinguishingCharacteristics",
    title: "Distinguishing Characteristics",
    columns: [
      { key: "id", label: "S.No" },
      { key: "characteristicType", label: "Characteristic Type" },
      { key: "location", label: "Location" },
      { key: "nist", label: "NIST" },
      { key: "description", label: "Description" },
    ],
  },
  {
    key: "arrests",
    title: "Arrests",
    columns: [
      { key: "bookingNumber", label: "Booking Number" },
      { key: "incarcerationDate", label: "Incarceration Date" },
      { key: "releaseDate", label: "Release Date" },
      { key: "housingLocation", label: "Current Location" },
    ],
  },
  {
    key: "holds",
    title: "Holds",
    columns: [
      { key: "holdReason", label: "Hold Reason" },
      { key: "holdFor", label: "Hold For" },
      { key: "holdContact", label: "Hold Contact" },
      { key: "holdContactPhone", label: "Hold Contact Phone" },
      { key: "expiresDate", label: "Expires Date" },
      { key: "expiresTime", label: "Expires Time" },
      { key: "comments", label: "Comments" },
    ],
  },
  {
    key: "victimNotifications",
    title: "Victim Notifications",
    columns: [
      { key: "status", label: "Status" },
      { key: "name", label: "Name" },
      { key: "bookingNumber", label: "Booking Number" },
      { key: "comments", label: "Comments" },
    ],
  },
  {
    key: "notificationsAsVictim",
    title: "Notifications as Victim",
    columns: [
      { key: "bookingNumber", label: "Booking #" },
      { key: "inmateName", label: "Inmate Name" },
      { key: "status", label: "Status" },
    ],
  },
  {
    key: "inmateAssociates",
    title: "Inmate Associates",
    columns: [
      { key: "bookingNumber", label: "Booking_Number" },
      { key: "associateType", label: "Associate Type" },
      { key: "associateName", label: "Associate Name" },
      { key: "jacketId", label: "JacketID" },
    ],
  },
  {
    key: "associatedWithInmates",
    title: "Associated with Inmates",
    columns: [
      { key: "bookingNumber", label: "Booking #" },
      { key: "associateType", label: "Associate Type" },
      { key: "bookingName", label: "Booking Name" },
      { key: "jacketId", label: "JacketID" },
    ],
  },
  {
    key: "keepSeparate",
    title: "Keep Separate from Inmates",
    columns: [
      { key: "bookingNumber", label: "Booking #" },
      { key: "bookingName", label: "Booking Name" },
    ],
  },
  {
    key: "warrants",
    title: "Warrants",
    columns: [
      { key: "warrantNumber", label: "Warrant Number" },
      { key: "status", label: "Status" },
      { key: "charge", label: "Charge" },
      { key: "dateIssued", label: "Date Issued" },
    ],
  },
  {
    key: "addressBook",
    title: "Address Book Entries",
    columns: [
      { key: "name", label: "Name" },
      { key: "type", label: "Type" },
      { key: "address", label: "Address" },
    ],
  },
  {
    key: "civilPapers",
    title: "Civil Papers",
    columns: [
      { key: "crn", label: "CRN" },
      { key: "documentType", label: "Document Type" },
      { key: "dateReceived", label: "Date Received" },
      { key: "status", label: "Status" },
    ],
  },
  {
    key: "civilPaperServiceAttempts",
    title: "Civil Paper Document Service Attempts",
    columns: [
      { key: "crn", label: "Case #" },
      { key: "defendant", label: "Defendant" },
      { key: "servedName", label: "Served Name" },
      { key: "attemptDate", label: "Attempt Date" },
      { key: "attemptTime", label: "Attempt Time" },
    ],
  },
  {
    key: "staffRecords",
    title: "Staff records",
    columns: [
      { key: "name", label: "Name" },
      { key: "rank", label: "Rank" },
      { key: "badge", label: "Badge" },
      { key: "division", label: "Division" },
    ],
  },
  {
    key: "transportRecords",
    title: "Transport records",
    columns: [
      { key: "date", label: "Date" },
      { key: "from", label: "From" },
      { key: "to", label: "To" },
      { key: "officer", label: "Officer" },
    ],
  },
  {
    key: "incidentReports",
    title: "Incident Reports",
    columns: [
      { key: "reportNumber", label: "Report #" },
      { key: "date", label: "Date" },
      { key: "type", label: "Type" },
      { key: "officer", label: "Officer" },
    ],
  },
  {
    key: "visitorLogMyVisitors",
    title: "Visitor Log (My Visitors)",
    columns: [
      { key: "fullName", label: "Full Name" },
      { key: "reasonForVisit", label: "Reason For Visit" },
      { key: "scheduled", label: "Scheduled Date/Time" },
      { key: "arrival", label: "Arrival Date/Time" },
      { key: "departure", label: "Departure Date/Time" },
    ],
  },
  {
    key: "visitorLogMyVisits",
    title: "Visitor Log (My Visits)",
    columns: [
      { key: "visitorName", label: "Visitor Name" },
      { key: "scheduled", label: "Scheduled Date/Time" },
      { key: "arrival", label: "Arrival Date/Time" },
      { key: "departure", label: "Departure Date/Time" },
    ],
  },
];

// Note: the extra plain classNames below ("section-header" etc.) are
// deliberate, not redundant with the styled-components — Print (see
// buildJacketReportPrintHtml) captures this markup's innerHTML into a
// *separate* HTML document that has none of the app's styled-components
// stylesheet, so its inline <style> block has to target plain, stable
// class names.
//
// Titles, column headings and the empty message go through the site's
// Vocabulary renames, as PCI's JacketSummary.asp did with Vocab("ARRESTS"),
// Vocab("WARRANTS"), Vocab("Charges") etc.
export const ReportSection = ({ title, columns, rows }) => {
  const vocab = useVocab();
  return (
    <ReportSectionBlock>
      <ReportSectionHeaderBar className="section-header">
        {vocab(title)}
      </ReportSectionHeaderBar>
      {rows?.length ? (
        <ReportSectionTable className="section-table">
          <thead>
            <tr>
              {columns.map((c) => (
                <th key={c.key}>{vocab(c.label)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, idx) => (
              <tr key={r?.id ?? idx}>
                {columns.map((c) => (
                  <td key={c.key}>{r?.[c.key] ?? ""}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </ReportSectionTable>
      ) : (
        <ReportEmptyMessage className="section-empty">
          {vocab("There is no data to display.")}
        </ReportEmptyMessage>
      )}
    </ReportSectionBlock>
  );
};

const ARREST_CHARGE_COLUMNS = [
  { key: "chargeDescription", label: "Charge" },
  { key: "level", label: "Lvl" },
  { key: "disposition", label: "Disposition" },
];

const ARREST_DETENTION_COLUMNS = [
  { key: "id", label: "Det. #" },
  { key: "start", label: "Start" },
  { key: "agency", label: "Agency" },
  { key: "end", label: "End" },
  { key: "releaseReason", label: "Release Reason" },
];

// Arrests — special-cased vs. the generic ReportSection because ASP nests a
// Charges table *and* a Detentions ("Det. #") table under each individual
// booking, not one flat table of bookings (see reportSectionsApi.js/
// fetchJacketArrests for exactly what's real vs. the known Disposition
// gap). Each `rows` entry is one booking with its own charges[]/
// detentions[] already attached.
export const ArrestsSection = ({ rows }) => {
  const vocab = useVocab();
  return (
    <ReportSectionBlock>
      <ReportSectionHeaderBar className="section-header">
        {vocab("Arrests")}
      </ReportSectionHeaderBar>
      {rows?.length ? (
        rows.map((arrest, idx) => (
          <ArrestBlock
            className="arrest-block"
            key={arrest?.bookingNumber ?? idx}
          >
            <ReportSectionTable className="section-table">
              <thead>
                <tr>
                  <th>{vocab("Booking Number")}</th>
                  <th>{vocab("Incarceration Date")}</th>
                  <th>{vocab("Arresting ORI")}</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>{arrest?.bookingNumber ?? ""}</td>
                  <td>{arrest?.incarcerationDate ?? ""}</td>
                  <td>{arrest?.agency ?? ""}</td>
                </tr>
              </tbody>
            </ReportSectionTable>

            <ReportSection
              title="Charges"
              columns={ARREST_CHARGE_COLUMNS}
              rows={arrest?.charges}
            />
            <ReportSection
              title="Detentions"
              columns={ARREST_DETENTION_COLUMNS}
              rows={arrest?.detentions}
            />
          </ArrestBlock>
        ))
      ) : (
        <ReportEmptyMessage className="section-empty">
          {vocab("There is no data to display.")}
        </ReportEmptyMessage>
      )}
    </ReportSectionBlock>
  );
};

// Renders every SECTION_DEFS entry in order, special-casing "arrests" to
// ArrestsSection above. Shared by every report entry point (Jackets/
// Bookings/Warrants module summaries) so a future new section only has to
// be added here once.
export const ReportSectionList = ({ sectionRows }) => (
  <>
    {SECTION_DEFS.map((def) =>
      def.key === "arrests" ? (
        <ArrestsSection key={def.key} rows={sectionRows.arrests} />
      ) : (
        <ReportSection
          key={def.key}
          title={def.title}
          columns={def.columns}
          rows={sectionRows[def.key] || []}
        />
      ),
    )}
  </>
);

// Bio + photo header block — matches the top of reports/JacketSummaryNew.asp
// (name/alias row, PIN/race/sex, address/height/weight, hair/eyes,
// glasses/length, phone/facial/complexion, DL#/DOB, JacketID/SSN, photo).
// Takes an already-resolved `bio` object so callers can hand it whatever
// shape their endpoint returns it in (Jackets search nests it under
// row.originalData.jackets.bio[0]; the booking-detail endpoint returns it
// as detail.bio[0]) — the inner Bio field shape itself (bioAddresses,
// bioAliases, bioPhotos, etc.) is the same either way.
export const ReportBioHeader = ({ bio, jacketId }) => {
  // PCI's JacketSummary.asp shows Vocab("PIN"): a site's rename (e.g. "Old CRH#").
  // The other labels go through Vocab by text only, so a site can rename them too.
  const vocab = useVocab();
  const address = bio?.bioAddresses?.[0]?.addresses?.[0] || {};
  const aliasList = (bio?.bioAliases || []).filter((a) => a?.lName || a?.fName);
  const photoPath = bio?.bioPhotos?.[0]?.photos?.[0]?.fullSizePath;
  const photoUrl = photoPath
    ? PHOTOS_BASE_URL + getPhotoPath(photoPath)
    : defaultAvatar;

  return (
    <table className="bio-header-table">
      <tbody>
        <tr>
          <td colSpan={5} className="name-row">
            {capitalize(bio?.fName)}{" "}
            {bio?.mName ? capitalize(bio.mName) + " " : ""}
            {capitalize(bio?.lName)} {bio?.suffix || ""}
          </td>
          <td rowSpan={9} style={{ width: 150 }}>
            <img
              alt="Folder"
              src={photoUrl}
              style={{ width: "100%", maxWidth: 150, border: "1px solid #999" }}
              onError={(e) => {
                e.currentTarget.src = defaultAvatar;
              }}
            />
          </td>
        </tr>
        <tr>
          <td colSpan={5}>
            <span className="label">{vocab("Alias")}: </span>
            {aliasList.length
              ? aliasList
                  .map((a) =>
                    [a?.lName, a?.fName, a?.mName].filter(Boolean).join(" "),
                  )
                  .join("; ")
              : ""}
          </td>
        </tr>
        {/* Every paired field below (Race/Sex, Height/Weight, Hair/Eyes,
            Glasses/Length, Facial/Complexion) uses the same 2-cell
            label+value split as the other, so both value columns line up
            in their own vertical column instead of trailing the label
            text at whatever width it happens to render. */}
        <tr>
          <td>
            <span className="label">{vocab("PIN", "pinNo")}: </span>
            {bio?.pinNo || ""}
          </td>
          <td>
            <span className="label">{vocab("Race")}:</span>
          </td>
          <td>{bio?.race || ""}</td>
          <td>
            <span className="label">{vocab("Sex")}:</span>
          </td>
          <td>{bio?.sex || ""}</td>
        </tr>
        <tr>
          <td rowSpan={3}>
            {address?.addressStreetNumber
              ? `${address.addressStreetNumber} ${address.addressStreet1 || ""}`
              : ""}
            <br />
            {address?.addressCity
              ? `${address.addressCity}, ${address.addressType || ""}`
              : ""}
          </td>
          <td>
            <span className="label">{vocab("Height")}:</span>
          </td>
          <td>{bio?.height || ""}</td>
          <td>
            <span className="label">{vocab("Weight")}:</span>
          </td>
          <td>{bio?.weight || ""}</td>
        </tr>
        <tr>
          <td>
            <span className="label">{vocab("Hair")}:</span>
          </td>
          <td>{bio?.hair || ""}</td>
          <td>
            <span className="label">{vocab("Eyes")}:</span>
          </td>
          <td>{bio?.eyes || ""}</td>
        </tr>
        <tr>
          <td>
            <span className="label">{vocab("Glasses")}:</span>
          </td>
          <td>{bio?.glasses || ""}</td>
          <td>
            <span className="label">{vocab("Length")}:</span>
          </td>
          <td>{bio?.hairLength || ""}</td>
        </tr>
        <tr>
          <td>
            <span className="label">{vocab("Phone")}:</span> {bio?.phone || ""}
          </td>
          <td>
            <span className="label">{vocab("Facial")}:</span>
          </td>
          <td>{bio?.facialHair || ""}</td>
          <td>
            <span className="label">{vocab("Complexion")}:</span>
          </td>
          <td>{bio?.complexion || ""}</td>
        </tr>
        <tr>
          <td>
            <span className="label">{vocab("Driver's License")}:</span>{" "}
            {bio?.dlNumber || ""}
          </td>
          <td>
            <span className="label">{vocab("DOB")}:</span>
          </td>
          <td colSpan={3}>{bio?.dob || ""}</td>
        </tr>
        <tr>
          <td>
            <span className="label">{vocab("JacketID")}:</span> {jacketId ?? ""}
          </td>
          <td>
            <span className="label">{vocab("SSN")}:</span>
          </td>
          <td colSpan={3}>{bio?.ssn || ""}</td>
        </tr>
      </tbody>
    </table>
  );
};

const EMPTY_BOOKING_SECTIONS = {
  arrests: [],
  holds: [],
  victimNotifications: [],
  inmateAssociates: [],
};

// Fetches every jacket-scoped section wired to real data right now (see the
// gap note on SECTION_DEFS above for what isn't) for a given jacketId.
// Shared by every report entry point so they all show the same data for
// the same person.
export const useJacketReportSections = (jacketId) => {
  const [loading, setLoading] = useState(true);
  const [bookingSections, setBookingSections] = useState(
    EMPTY_BOOKING_SECTIONS,
  );
  const [warrants, setWarrants] = useState([]);

  useEffect(() => {
    if (!jacketId) {
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);

    Promise.allSettled([
      fetchJacketBookingSections(jacketId),
      fetchJacketWarrants(jacketId),
    ]).then(([bookingResult, warrantsResult]) => {
      if (cancelled) return;
      setBookingSections(
        bookingResult.status === "fulfilled"
          ? bookingResult.value
          : EMPTY_BOOKING_SECTIONS,
      );
      setWarrants(
        warrantsResult.status === "fulfilled" ? warrantsResult.value : [],
      );
      setLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [jacketId]);

  const sectionRows = useMemo(
    () => ({
      // fetchJacketBookingSections already returns each booking pre-shaped
      // with its own charges[]/detentions[] attached (see
      // reportSectionsApi.js) — ArrestsSection consumes that shape
      // directly, unlike every other section here which is a flat row
      // list already matching its SECTION_DEFS columns.
      arrests: bookingSections.arrests,
      holds: bookingSections.holds,
      victimNotifications: bookingSections.victimNotifications,
      inmateAssociates: bookingSections.inmateAssociates,
      warrants: warrants.map((w) => ({
        warrantNumber: w?.warrantNumber,
        status: w?.status,
        charge: w?.charge,
        dateIssued: w?.dateIssued,
      })),
    }),
    [bookingSections, warrants],
  );

  return { loading, sectionRows };
};

// Distinguishing Characteristics → ASP's columns (Characteristic Type/
// Location/NIST/Description). Field names and the "DistinguishingCharacter
// istics"/"CharacteristicLocation" ref lists confirmed against
// PersonalInformationDetails/DistinguishingCharacteristicsForm.jsx —
// characteristicType/location are stored as ref IDs (Select-bound), nist/
// description are plain text. Lives on `bio`, not the jacket-scoped
// sections above, so callers pass it in separately (they already have
// `bio` locally either way — see JacketFullSummaryReport/
// BookingSummaryReport).
export const mapDistinguishingCharacteristics = (list) =>
  (list || []).map((dc, idx) => ({
    id: dc?.distinguishingCharacteristicsId ?? dc?.id ?? idx,
    characteristicType: dc?.characteristicType,
    location: dc?.location,
    nist: dc?.nist,
    description: dc?.description,
  }));

// Shared print-window HTML shell so every "full jacket report" entry point
// (Jackets/Bookings/Warrants module summaries) prints identically.
export const buildJacketReportPrintHtml = (title, bodyHtml) => `
  <html>
    <head>
      <title>${title}</title>
      <style>
        body { font-family: "Times New Roman", serif; padding: 20px; color: #000; font-size: 12px; }
        h2 { font-size: 16px; margin-bottom: 12px; }
        table.bio-header-table { width: 100%; border-collapse: collapse; margin-bottom: 12px; }
        table.bio-header-table td { vertical-align: top; padding: 2px 6px; }
        .label { font-weight: 700; }
        .name-row { font-size: 15px; font-weight: 700; }
        .section-header { background: #d9d9d9; font-weight: 700; padding: 3px 6px; margin-top: 6px; }
        .section-empty { padding: 4px 6px; }
        table.section-table { width: 100%; border-collapse: collapse; margin-bottom: 4px; }
        table.section-table th, table.section-table td { border: 1px solid #999; padding: 4px 6px; text-align: left; }
        table.section-table th { background: #f0f0f0; }
        .arrest-block { margin: 4px 0 10px 0; padding-left: 12px; border-left: 3px solid #999; }
        .arrest-block .section-header { background: #eef1f4; font-size: 11px; text-transform: uppercase; }
      </style>
    </head>
    <body>
      <h2>${title}</h2>
      ${bodyHtml}
    </body>
  </html>
`;
